import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { GetCartsApiParameter } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartsDataView } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCarts from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCarts";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import baseTheme from "@insite/mobius/globals/baseTheme";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import Link, { StyledIcon } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { HorizontalRule } from "@ucs/Components/HorizontalRule";
import { formatDateByYearMonthDay } from "@ucs/Helpers/Formatting";
import Checkpoint from "@ucs/Icons/Checkpoint";
import ChevronRight from "@ucs/Icons/ChevronRight";
import SettingsWheel from "@ucs/Icons/SettingsWheel";
import Truck from "@ucs/Icons/Truck";
import UCSColors from "@ucs/Utils/UCSColors";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    numberOfRecords = "numberOfRecords",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        getSavedOrdersParameter: state.pages.savedOrderList.getCartsApiParameter,
        savedOrdersDataView: getCartsDataView(state, savedOrdersParameter),
        savedOrdersPageLink: getPageLinkByPageType(state, "SavedOrderListPage"),
        savedOrderDetailsLink: getPageLinkByPageType(state, "SavedOrderDetailsPage"),
        settingsCollection: getSettingsCollection(state),
        canViewOrders: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
        language: state.context.session.language,
    };
};

const mapDispatchToProps = {
    loadCarts,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const enum IconStatuses {
    Processing = "Processing",
    Delivering = "Delivering",
    Received = "Received",
}
const iconMap = new Map();
const Icons = {
    Processing: {
        icon: <StyledIcon src={SettingsWheel} size={24} color="#FF9900" />,
        text: IconStatuses.Processing,
    },
    Delivering: {
        icon: <StyledIcon src={Truck} size={24} color="#40A9FF" />,
        text: IconStatuses.Delivering,
    },
    Received: {
        icon: <StyledIcon src={Checkpoint} size={24} color="#1D8F6A" />,
        text: IconStatuses.Received,
    },
};
iconMap.set(Icons.Processing.text, Icons.Processing.icon);
iconMap.set(Icons.Delivering.text, Icons.Delivering.icon);
iconMap.set(Icons.Received.text, Icons.Received.icon);

export interface SavedOrdersStyles {
    errorGridItem?: GridItemProps;
    errorText?: TypographyPresentationProps;
    noOrdersFoundGridItem?: GridItemProps;
    noOrdersFoundText?: TypographyPresentationProps;
    orderMainContainer: any;
    dataTable: any;
    viewAllButton: any;
    containerName: any;
    topContainerTile: any;
    topHeadingContainer: any;
    topDataContainer: any;
    listNameLink: any;
    wishlistLineItem: any;
}

export const SavedOrdersStyles: SavedOrdersStyles = {
    errorGridItem: { width: 12 },
    noOrdersFoundGridItem: { width: 12 },
    orderMainContainer: css`
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        padding: 20px;
    `,
    topHeadingContainer: {
        css: css`
            display: inline-flex;
            flex-direction: row;
            width: 100%;
            margin-bottom: 8px !important;
        `,
    },
    topContainerTile: {
        css: css`
            background: #ffffff;
            padding: 10px;
            padding-left: 20px;
            padding-right: 20px;
            box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.05);
            margin-bottom: 8px;
        `,
    },
    containerName: {
        css: css`
            display: flex;
            flex: 1;
            justify-content: flex-start;
        `,
    },
    viewAllButton: {
        css: css`
            display: flex;
            flex: 1;
            justify-content: flex-end;
        `,
    },
    topDataContainer: {
        css: css``,
    },
    listNameLink: {
        css: css`
            color: ${UCSColors.green00};
            white-space: no-wrap;
        `,
    },
    wishlistLineItem: {
        css: css`
            gap: 10px;
        `,
    },
    dataTable: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            #orderNumId a span {
                color: ${UCSColors.green00} !important;
            }
            ${({ theme }) => {
                const { maxWidths } = theme.breakpoints || baseTheme.breakpoints;
                return breakpointMediaQueries(theme, [
                    css`
                        max-width: ${maxWidths[1]}px;
                        #shipToId {
                            order: 2 !important;
                        }
                        #orderDateId {
                            order: 1 !important;
                        }
                        #totalAmountId {
                            order: 3 !important;
                        }
                        #statusId {
                            order: 4 !important;
                        }
                    `,
                    css`
                        max-width: ${maxWidths[2]}px;
                        #shipToId {
                            order: 2 !important;
                        }
                        #orderDateId {
                            order: 1 !important;
                        }
                        #totalAmountId {
                            order: 3 !important;
                        }
                        #statusId {
                            order: 4 !important;
                        }
                    `,
                    css`
                        max-width: ${maxWidths[3]}px;
                    `,
                    css`
                        max-width: ${maxWidths[4]}px;
                    `,
                    css`
                        max-width: ${maxWidths[5]}px;
                    `,
                ]);
            }}
        `,
        cell: {
            css: css`
                display: flex;
                flex: 1;
                flex-direction: column;
                align-self: auto;
                justify-self: center;
                color: ${UCSColors.gray03};
            `,
        },
        header: {
            css: css`
                display: flex;
                flex: 1;
                flex-direction: column;
                align-self: auto;
                justify-self: auto;
                text-transform: uppercase;
                font-weight: bold;
                color: ${UCSColors.gray04};
            `,
        },
        headerContainer: {
            css: css`
                display: flex;
                flex-direction: row;
                width: 100%;
                gap: 40px;
            `,
        },
        row: {
            css: css`
                display: flex;
                flex: 1;
                flex-direction: row;
                gap: 40px;
                padding-top: 2px;
                padding-bottom: 2px;
            `,
        },
        previewImages: {
            css: css`
                display: flex;
                flex-direction: row;
                flex: 1;
                gap: 10px;
            `,
        },
        mobilePreviewImages: {
            css: css`
                display: flex;
                flex-direction: row;
                flex: 1;
                gap: 10px;
            `,
        },
        mobileCell: {
            css: css`
                display: flex;
                flex-direction: column;
                white-space: no-wrap;
                color: ${UCSColors.gray03};
            `,
        },
        mobileHeader: {
            css: css`
                display: flex;
                flex-direction: column;
                align-self: center;
                justify-content: flex-start;
                text-transform: uppercase;
                font-weight: bold;
                color: ${UCSColors.gray04};
            `,
        },
        mobileHeaderContainer: {
            css: css`
                display: flex;
                flex-direction: column;
            `,
        },

        mobileRow: {
            css: css`
                display: flex;
                flex-direction: column;
                justify-content: flex-start;
                align-self: start;
            `,
        },
        innerTwoGrid: {
            css: css`
                display: grid;
                grid-template-columns: [first] 86px [line2] auto;
                grid-auto-flow: column;
                align-items: start;
            `,
        },
        linkItem: {
            css: css`
                span { 
                    color: ${UCSColors.green00};
                }
            `,
        },
    },
};

let savedOrdersParameter: GetCartsApiParameter = {
    sort: "OrderDate DESC",
    status: "Saved"
};

const styles = SavedOrdersStyles;
class SavedOrdersTable extends React.Component<Props> {
    componentDidMount() {
        savedOrdersParameter.pageSize = this.props.fields.numberOfRecords;
        if (this.props.canViewOrders && !this.props.savedOrdersDataView.value) {
            this.props.loadCarts({ apiParameter: savedOrdersParameter });
        }
    }

    getOrderStatusIcon = (status: string) => {
        return iconMap.get(status);
    };

    render() {
        const { savedOrdersDataView, savedOrdersPageLink, settingsCollection, canViewOrders, language, savedOrderDetailsLink } = this.props;
        if (!canViewOrders || !savedOrdersDataView.value) {
            return null;
        }
        const savedOrdersListUrl = savedOrdersPageLink ? savedOrdersPageLink.url : undefined;
        if (savedOrdersDataView === null) {
            return;
        }

        const savedOrdersData = savedOrdersDataView.value;
        return (
            <StyledWrapper {...styles.topContainerTile} data-test-selector="myAccountSavedOrdersTable">
                <StyledWrapper {...styles.topHeadingContainer}>
                    <Typography size={28} {...styles.containerName}>
                        {translate("Saved Orders")}
                    </Typography>
                    <Link
                        href={savedOrdersListUrl}
                        target="_self"
                        icon={{
                            iconProps: {
                                color: `${UCSColors.green00}`,
                                size: 16,
                                src: ChevronRight,
                            },
                        }}
                    >
                        <Typography size={18} {...styles.viewAllButton}>
                            {translate("View all ")}
                        </Typography>
                    </Link>
                </StyledWrapper>
                <StyledWrapper role="presentation" {...styles.dataTable}>
                    <Hidden below="md">
                        <HorizontalRule />
                        <StyledWrapper {...styles.dataTable.headerContainer}>
                            <StyledWrapper id="orderDateId" {...styles.dataTable.header}>
                                <Typography size={13}>Order Date</Typography>
                            </StyledWrapper>
                            <StyledWrapper id="shipToId" {...styles.dataTable.header}>
                                <Typography size={13}>Ship To / Pick Up</Typography>
                            </StyledWrapper>
                            <StyledWrapper id="totalAmountId" {...styles.dataTable.header}>
                                <Typography size={13}>{translate("Total")}</Typography>
                            </StyledWrapper>
                            {/* UFG-420 */}
                            {/* <StyledWrapper id="statusId" {...styles.dataTable.header}>
                                <Typography size={13}>Status</Typography>
                            </StyledWrapper> */}
                        </StyledWrapper>
                        <HorizontalRule />
                    </Hidden>
                    <Hidden above="sm">
                        <HorizontalRule />
                    </Hidden>
                    {savedOrdersData.length === 0 && (
                        <GridItem {...styles.noOrdersFoundGridItem}>
                            <Typography {...styles.noOrdersFoundText}>
                                {siteMessage("Orders_NoOpenOrders")}
                            </Typography>
                        </GridItem>
                    )}
                    {savedOrdersData.map((order, index) => {
                        if (order !== null) {
                            const row = {
                                id: order.id,
                                orderDate: order.orderDate ? getLocalizedDateTime({
                                    dateTime: new Date(order.orderDate),
                                    language,
                                }) : "",
                                shipToLabel: order.collectionShipToLabel ?? order.shipToLabel,
                                total: order.orderSubTotalDisplay
                                // UFG-420
                                // status: order.statusDisplay,
                            }
                            return (
                                <StyledWrapper key={row.id}>
                                    <StyledWrapper {...styles.dataTable.row} role="row">
                                        <Hidden below="md" {...styles.dataTable.row}>
                                            <StyledWrapper id="orderDateId" {...styles.dataTable.cell}>
                                                <Link {...styles.dataTable.linkItem} href={`${savedOrderDetailsLink?.url}?cartId=${row.id}`}>
                                                    <Typography size={18}>{formatDateByYearMonthDay(new Date(row.orderDate))}</Typography>
                                                </Link>
                                            </StyledWrapper>
                                            <StyledWrapper id="shipToId" {...styles.dataTable.cell}>
                                                <Typography size={18}>{row.shipToLabel}</Typography>
                                            </StyledWrapper>
                                            <StyledWrapper id="totalAmountId" {...styles.dataTable.cell}>
                                                <Typography size={18}>{row.total}</Typography>
                                            </StyledWrapper>
                                            {/* UFG-420 */}
                                            {/* <StyledWrapper id="statusId" {...styles.dataTable.cell}>
                                                <Typography size={18}>{row.status}</Typography>
                                            </StyledWrapper> */}
                                        </Hidden>
                                        <Hidden above="sm" {...styles.dataTable.mobileRow}>
                                            <StyledWrapper id="orderDateId" {...styles.dataTable.mobileCell}>
                                                <StyledWrapper {...styles.dataTable.innerTwoGrid}>
                                                    <Typography {...styles.dataTable.mobileHeader} size={10}>
                                                        {translate("Order Date")}
                                                    </Typography>
                                                    <StyledWrapper {...styles.dataTable.mobileCell}>
                                                        <Link {...styles.dataTable.linkItem} href={`${savedOrderDetailsLink?.url}?cartId=${row.id}`}>
                                                            <Typography size={18}>{formatDateByYearMonthDay(new Date(row.orderDate))}</Typography>
                                                        </Link>
                                                    </StyledWrapper>
                                                </StyledWrapper>
                                            </StyledWrapper>
                                            <StyledWrapper id="shipToId" {...styles.dataTable.mobileCell}>
                                                <StyledWrapper {...styles.dataTable.innerTwoGrid}>
                                                    <Typography {...styles.dataTable.mobileHeader} size={10}>Ship To / Pick Up</Typography>
                                                    <Typography size={14}>{row.shipToLabel}</Typography>
                                                </StyledWrapper>
                                            </StyledWrapper>
                                            <StyledWrapper id="totalAmountId" {...styles.dataTable.mobileCell}>
                                                <StyledWrapper {...styles.dataTable.innerTwoGrid}>
                                                    <Typography {...styles.dataTable.mobileHeader} size={10}>
                                                        {translate("Total")}
                                                    </Typography>
                                                    <Typography size={14}>{row.total}</Typography>
                                                </StyledWrapper>
                                            </StyledWrapper>
                                            {/* UFG-420 */}
                                            {/* <StyledWrapper id="statusId" {...styles.dataTable.mobileCell}>
                                                <StyledWrapper {...styles.dataTable.innerTwoGrid}>
                                                    <Typography {...styles.dataTable.mobileHeader} size={10}>
                                                        Status
                                                    </Typography>
                                                    <Typography size={14}>
                                                        {this.getOrderStatusIcon(row.status)}
                                                        {row.status}
                                                    </Typography>
                                                </StyledWrapper>
                                            </StyledWrapper> */}
                                        </Hidden>
                                    </StyledWrapper>
                                    <HorizontalRule />
                                </StyledWrapper>
                            );
                        }
                        return "";
                    })}
                </StyledWrapper>
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(SavedOrdersTable),
    definition: {
        group: "Common",
        displayName: "Saved Orders Table",
        icon: "List",
        fieldDefinitions: [
            {
                name: fields.numberOfRecords,
                displayName: "Number of Open Orders Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;